import { FC, useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { TabbedContainer, useAlert, useSimpleTabState } from "capitalrx-components-2.0/lib/general";
import { formatDate } from "capitalrx-utilities";

import { getMemberDocumentUrl, getPlanDocumentUrl, useListMemberDocumentsQuery, useListPlanDocumentsQuery } from "@/App/api/documents";
import { ContentPanel, PageWrapper, ThemedTabbedContainer } from "@/App/components";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";
import { DocumentTab, DocumentTabDocument } from "./DocumentTab";
import { MemberDocumentRequest } from "@/App/types/documents";
import { AuthenticationContext } from "@/App/context";

export const DocumentsPage: FC = () => {
  const { t: translate } = useTranslation();
  const { data: rawMemberDocuments, isLoading: isLoadingMemberDocs } = useListMemberDocumentsQuery();
  const { data: rawPlanDocuments, isLoading: isLoadingPlanDocs } = useListPlanDocumentsQuery();
  const { getAuthToken } = useContext(AuthenticationContext);
  const [selectedTabId, setSelectedTabId] = useSimpleTabState();
  const alert = useAlert();

  const planDocuments: DocumentTabDocument[] = useMemo(() => {
    if (rawPlanDocuments) {
      return rawPlanDocuments.results.map((rawDoc) => {
        return {
          id: rawDoc.document.id,
          name: rawDoc.document.name,
          description: rawDoc.document.description,
          documentSource: "internal",
          documentType: rawDoc.document.document_type,
          submissionId: rawDoc.document.submission_id,
        };
      });
    } else {
      return [];
    }
  }, [rawPlanDocuments]);

  const memberDocuments: DocumentTabDocument[] = useMemo(() => {
    if (rawMemberDocuments) {
      return rawMemberDocuments.results.map((rawDoc) => {
        const source = rawDoc.document.external_document_id ? "external" : "internal";
        return {
          id: source === "external" ? rawDoc.document.external_document_id : rawDoc.document.id,
          name: source === "external" ? rawDoc.document.document_type : rawDoc.document.name,
          description: source === "external" ? formatDate(rawDoc.document.document_sent_date, "MMMM D, YYYY") : rawDoc.document.description,
          documentSource: source,
          documentType: rawDoc.document.document_type,
          submissionId: rawDoc.document.submission_id,
          submissionConfirmationNumber: rawDoc.document.submission_confirmation_number,
        };
      });
    } else {
      return [];
    }
  }, [rawMemberDocuments]);

  const onMemberDocDownload = useCallback(
    async (memberDoc: DocumentTabDocument) => {
      try {
        const downloadBody: MemberDocumentRequest = {
          "document_type": memberDoc.documentType,
        };
        if(memberDoc.documentSource === "external") {
          downloadBody.external_document_id = memberDoc.id.toString();
        } else {
          downloadBody.id = +memberDoc.id;
          downloadBody.submission_id = +memberDoc.submissionId;
        }
        const downloadResponse = await getMemberDocumentUrl(await getAuthToken(), downloadBody);
        logEvent(ANALYTIC_EVENTS.DOCUMENTS.download, { memberDocumentId: memberDoc.id, memberDocumentName: memberDoc.name });
        window.open(downloadResponse.url, "_parent");
      } catch (e) {
        console.error(e);
        alert({
          message: `${translate("DOCUMENTS_PAGE.couldNotDownloadDocumentAlert")}`,
          type: "error",
          style: "banner",
        });
      }
    },
    [memberDocuments],
  );

  const onPlanDocumentDownload = useCallback(
    async (planDoc: DocumentTabDocument) => {
      try {
        const downloadResponse = await getPlanDocumentUrl(await getAuthToken(), { id: planDoc.id.toString() });
        logEvent(ANALYTIC_EVENTS.DOCUMENTS.download, { memberDocumentId: planDoc.id.toString(), memberDocumentName: planDoc.name });
        window.open(downloadResponse.url, "_parent");
      } catch (e) {
        alert({
          message: `${translate("DOCUMENTS_PAGE.couldNotDownloadDocumentAlert")}`,
          type: "error",
          style: "banner",
        });
      }
    },
    [planDocuments],
  );

  return (
    <PageWrapper>
      <ContentPanel title={translate("DOCUMENTS_PAGE.formsAndDocuments")} showBorder={false}>
        <ThemedTabbedContainer testId="documents-tab-container" selectedTab={selectedTabId} onSelectedTabChange={setSelectedTabId}>
          <TabbedContainer.TabPane testId="member-documents" id="member-documents" label={translate("DOCUMENTS_PAGE.memberDocuments")}>
            <DocumentTab isLoading={isLoadingMemberDocs} documents={memberDocuments} onDownload={onMemberDocDownload} />
          </TabbedContainer.TabPane>
          <TabbedContainer.TabPane testId="plan-documents" id="plan-documents" label={translate("DOCUMENTS_PAGE.planDocuments")}>
            <DocumentTab isLoading={isLoadingPlanDocs} documents={planDocuments} onDownload={onPlanDocumentDownload} />
          </TabbedContainer.TabPane>
        </ThemedTabbedContainer>
      </ContentPanel>
    </PageWrapper>
  );
};
